import {
  AfterViewInit,
  Component,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import {
  BehaviorSubject,
  Subscription,
  map,
  merge,
  startWith,
  switchMap,
  throwError} from 'rxjs';
import { ListColumn } from 'src/app/app-modules-layout/list/list-column.model';
import { EquipmentAsyncService } from 'src/app/app-services-async/equipment-async.service';
import { WHNestedBomNodeDOM } from 'src/app/w-h-module/w-h-bom-node-crud-form/w-h-nested-node.model';
import {
  IWHBomNodeDTO,
  IWHBomNodeFlexPropertyDTO,
  IWHEquipmentDTO,
  IWHWorkStepDTO,
  WHAssetsENUM,
  WHFeatureKeyENUM,
  WHIconENUM,
  WHLoginDataService,
  WHToastService,
} from '@workheld/workheld-shared-lib';
import { WorkStepAsyncService } from 'src/app/app-services-async/work-step-async.service';
import { WHWorkStepCreateEditDialogActionData } from '../app-dialog.service';
import { MatTableDataSource } from '@angular/material/table';
import { WHEnumDataService } from 'src/app/app-services-helper/w-h-enum-helper.service';
import { EquipmentStore } from 'src/app/app-modules-pages/equipment/equipment.store';

@Component({
  selector: 'app-mat-dialog-add-equipment-node',
  templateUrl: './mat-dialog-add-equipment-node.component.html',
  styleUrls: ['./mat-dialog-add-equipment-node.component.scss'],
  standalone: false})
export class MatDialogAddEquipmentNodeComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  // DOM
  @ViewChild(MatPaginator, { static: true }) public paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) public sort: MatSort;

  private readonly equipmentStore = inject(EquipmentStore);
  private readonly ngxToastrService = inject(WHToastService);
  private readonly equipmentAsyncService: EquipmentAsyncService = inject(
    EquipmentAsyncService,
  );
  private readonly workStepAsyncService: WorkStepAsyncService =
    inject(WorkStepAsyncService);
  private readonly loginDataService: WHLoginDataService =
    inject(WHLoginDataService);

  dataSource = new MatTableDataSource<IWHEquipmentDTO>([]);
  resultsLength: number;
  rootBomNodeDOM: IWHBomNodeDTO;

  selectedBomNode: IWHBomNodeDTO;
  selectedEquipmentId: string;

  fetchingEquipment: boolean = false;
  assigningBomNode: boolean = false;

  // ENUMS
  public EQUIPMENT_STATUS: any = {};
  public BOM_NODE_TYPE: any = {};
  public BOM_NODE_STATUS: any = {};
  public enums: any = {};

  // SKILL MANAGEMENT
  hasSkillManagementEnabled: boolean = false;

  nodesMap: Map<string, WHNestedBomNodeDOM> = new Map();
  nodeTree: WHNestedBomNodeDOM;
  bomNodeDataSource2: MatTreeNestedDataSource<WHNestedBomNodeDOM> =
    new MatTreeNestedDataSource<WHNestedBomNodeDOM>();
  bomNodeDataSource = new MatTreeNestedDataSource<WHNestedBomNodeDOM>();
  flexProperties2DArray: Array<IWHBomNodeFlexPropertyDTO[]> = [[]];
  treeControl = new NestedTreeControl<WHNestedBomNodeDOM>(
    (node) => node.children,
  );
  equipmentNodeId: string = undefined;
  isAssignable: boolean = false;
  pageSize = 13;
  searchTerm = new BehaviorSubject('');
  subscriptions = new Subscription();

  public hasChild = (_: number, node: WHNestedBomNodeDOM) =>
    !!node.children && node.children.length > 0;

  columns: ListColumn[] = [
    { name: '', property: 'id', visible: true },
    {
      name: 'equipment.model.name',
      property: 'name',
      visible: true,
      isModelProperty: true},
  ] as ListColumn[];
  childLoading: boolean;
  selectedNodeId: string;
  bomnodesLoading: boolean;
  $ENUMS_OBJECT = signal(null);

  constructor(
    public matDialogRef: MatDialogRef<MatDialogAddEquipmentNodeComponent>,
    private whEnumDataService: WHEnumDataService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      customerId: string;
      workStepId: string;
    },
  ) {}

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnInit(): void {
    this.$ENUMS_OBJECT = this.whEnumDataService.$ENUMS_OBJECT;
    if (!this.data.customerId) {
      return;
    }

    this.hasSkillManagementEnabled =
      this.loginDataService.featureConfigMap$.value.get(
        WHFeatureKeyENUM.SKILL_MANAGEMENT,
      );

    this.subscriptions.add(
      merge(this.searchTerm, this.paginator.page)
        .pipe(
          startWith(''),
          switchMap(() => {
            this.fetchingEquipment = true;
            return this.equipmentAsyncService.getEquipmentList(
              this.data.customerId,
              {
                page: this.searchTerm.value ? 0 : this.paginator.pageIndex,
                pageSize: this.pageSize,
                term: this.searchTerm.value,
                sort: 'name',
                sortMode: 'ASC' as 'ASC' | 'DESC',
              },
            );
          }),
          map((res) => {
            this.fetchingEquipment = false;
            this.dataSource = new MatTableDataSource(res.equipments);
            if (this.searchTerm.value) this.paginator.pageIndex = 0;
            this.resultsLength = res.numTotalEntries;
          }),
        )
        .subscribe(),
    );
  }

  get visibleColumns() {
    return this.columns
      .filter((column) => column.visible)
      .map((column) => column.property);
  }

  selectEquipment(equipment: IWHEquipmentDTO) {
    this.equipmentStore.loadEquipmentNode({
      equipment,
      nodeId: equipment.rootBomNodeId});
    this.selectedBomNode = undefined;
    this.selectedEquipmentId = equipment?.id;
  }

  public handleNodeMenuClick(event: any) {
    this.selectedBomNode = event?.node;
  }

  onFilterChange(filter) {
    this.searchTerm.next(filter);
  }

  confirm() {
    this.assigningBomNode = true;
    const workStepId = this.data?.workStepId;
    const payload = {
      id: workStepId,
      bomNodeId: this.selectedBomNode.id} as IWHWorkStepDTO;

    // Assign BomNode to WorkStep
    this.workStepAsyncService.updateWorkStep(workStepId, payload).subscribe({
      next: (res) => {
        this.successCloseDialog(res);
      },
      error: (err) => {
        this.assigningBomNode = false;
        throwError(() => err);
      }});
  }

  successCloseDialog(response: any) {
    this.ngxToastrService.success('workstep.ui.updatesuccess.notification');

    this.assigningBomNode = false;
    this.matDialogRef.close({
      actionType: 'EDIT',
      workStepDOM: response} as WHWorkStepCreateEditDialogActionData);
  }

  cancel() {
    this.matDialogRef.close();
  }

  get equipmentIcon(): string {
    return WHIconENUM.EquipmentIcon as string;
  }

  get saveIcon(): string {
    return WHIconENUM.SaveIcon as string;
  }

  get cancelIcon(): string {
    return WHIconENUM.CancelIcon as string;
  }

  get personImgJPG(): string {
    return WHAssetsENUM.personImgJPG as string;
  }

  public get expandIcon(): string {
    return WHIconENUM.ExpandIcon as string;
  }

  public get showSnappedIcon(): string {
    return WHIconENUM.ShowSnappedIcon as string;
  }
  ngOnDestroy(): void {
    this.equipmentStore.resetStore();
  }
}
